import type { GroupPricingStructure } from '../../api/group/groupAdminApiSlice';

/** One card in the price list: the cells that share one product's price list. */
export interface PriceCard {
    key: string;
    productId: string | null;
    groups: GroupPricingStructure[];
}

/**
 * The cards of the price list for one use case.
 *
 * A cell (`Group`) is (Product, Location), and for a room it IS the room
 * (ADR-0025, amendment 2026-09-28 (b)). So a room product with three rooms has
 * three cells pricing from ONE product list, and listing cells showed the same
 * table three times (PO, 2026-10-21). Room use cases are therefore carded per
 * product: the cells that share a product AND its price list. A cell without a
 * product, or one still pricing from a list of its own, stays its own card —
 * merging it would hide a price that differs.
 *
 * Tariff use cases (SUP, rental, storage) keep one card per cell: there a cell
 * is one location, and a location sheet makes its prices differ on purpose.
 */
export const priceCardsFor = (
    groups: ReadonlyArray<GroupPricingStructure>,
    byProduct: boolean,
): PriceCard[] => {
    const cards = new Map<string, PriceCard>();
    for (const group of groups) {
        const key = byProduct && group.productId
            ? `product:${group.productId}|${group.priceCategory?.id ?? ''}`
            : `cell:${group.id}`;
        const card = cards.get(key);
        if (card) card.groups.push(group);
        else cards.set(key, { key, productId: byProduct ? group.productId : null, groups: [group] });
    }
    return Array.from(cards.values());
};

/**
 * Whether a card's price list is also used OUTSIDE the card. A room product's
 * own rooms sharing its list is the card itself, not a surprise to flag.
 */
export const sharedBeyondCard = (card: PriceCard, categoryUse: ReadonlyMap<string, number>): boolean => {
    const categoryId = card.groups[0]?.priceCategory?.id;
    // A card without objects counts none: any cell on its list is outside it.
    return !!categoryId && (categoryUse.get(categoryId) ?? 0) > card.groups.length;
};

/**
 * A room product no object is assigned to yet has no cell, so no card — but
 * its prices are set BEFORE the first room is created (help T18 › Produkt
 * anlegen). It gets a card of its own, listing no objects, so the price list
 * stays the one place prices are set.
 */
export const withProductsWithoutObjects = (
    cards: PriceCard[],
    products: ReadonlyArray<{ id: string, useCase: string | null, compartmentTypes: string[] }>,
    service: string,
): PriceCard[] => {
    const carded = new Set(cards.map((card) => card.productId).filter(Boolean));
    const forService = (product: { useCase: string | null, compartmentTypes: string[] }) =>
        [product.useCase, ...product.compartmentTypes]
            .some((value) => !!value && value.toLowerCase() === service.toLowerCase());
    const extra = products
        .filter((product) => !carded.has(product.id) && forService(product))
        .map((product) => ({ key: `product:${product.id}|no-objects`, productId: product.id, groups: [] }));
    return [...cards, ...extra];
};
